import React from 'react';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import termsConstants from '../../../core-utils/messages/TermsConstants';
import { CheckBox, Close } from '@mui/icons-material';

export const cellStyles4 = {
  color: patientAccessTheme.palette.text.mediumemphasis,
  fontSize: '14px',
  fontWeight: 400,
};

export interface TermsModalProps {
  open: boolean;
  handleClose: any;
}

const TermsModal: React.FC<TermsModalProps> = ({ open, handleClose }) => {
  const useStyles = makeStyles()({
    modal: {
      width: '728px',
      height: '550px',
      background: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px 24px 24px 24px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
    },
    inputFieldsGrid: {
      width: 'full',
      marginTop: '20px',
      background: '#FBFBFE',
      padding: '14px 8px',
      borderRadius: '8px',
      overflow: 'auto',
      height: '400px',
      ...scrollBarStyles,
    },
    title: {
      backgroundColor: patientAccessTheme.palette.grey[300],
      padding: '3px 8px',
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
    },
    titleValue: {
      backgroundColor: patientAccessTheme.palette.grey[100],
      marginRight: '10px',
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
      padding: '3px 8px',
    },
  });
  const { classes } = useStyles();

  const constants = termsConstants;

  const highlightKeywords = (content: any, keywordsArray: any) => {
    let modifiedContent = content;

    keywordsArray.forEach((item: any) => {
      const regex = new RegExp(`(${item.key})`, 'gi'); // Case-insensitive global search
      modifiedContent = modifiedContent.replace(
        regex,
        `<a href="${item.link}" target="_blank" style="color: blue; text-decoration: underline;">$1</a>`,
      );
    });

    return modifiedContent;
  };

  const sectionContent = (content: any) => {
    if (!content) {
      return null;
    }

    return (
      <Typography
        fontSize="12px"
        lineHeight="19px"
        fontType="normal"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        dangerouslySetInnerHTML={{
          __html: highlightKeywords(content, constants.array),
        }}
      />
    );
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Grid container className={classes.modal}>
          <Grid>
            <Typography
              fontSize="22px"
              lineHeight="36px"
              fontType="bold"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin="4px 0px 0px 0px"
              styleProps={{
                textDecoration: 'underline',
                marginBottom: '10px',
              }}
            >
              {constants.title}
            </Typography>
            <Grid container>
              <Typography
                fontSize="10px"
                lineHeight="14px"
                fontType="normal"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                className={classes.title}
              >
                {constants.effectiveDateTitle}
              </Typography>
              <Typography
                fontSize="10px"
                lineHeight="14px"
                fontType="normal"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                className={classes.titleValue}
              >
                {constants.effectiveDate}
              </Typography>
              <Typography
                fontSize="10px"
                lineHeight="14px"
                fontType="normal"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                className={classes.title}
              >
                {constants.lastUpdtedTitle}
              </Typography>
              <Typography
                fontSize="10px"
                lineHeight="14px"
                fontType="normal"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                className={classes.titleValue}
              >
                {constants.lastUpdated}
              </Typography>
            </Grid>
          </Grid>

          <Grid container className={classes.inputFieldsGrid}>
            {constants.sections.map((section, index) => (
              <Grid
                key={index}
                style={{
                  marginBottom: '16px',
                }}
              >
                <Typography
                  fontSize="14px"
                  lineHeight="19px"
                  fontType="semibold"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  style={{ marginBottom: '4px' }}
                >
                  {section.title}
                </Typography>
                {section.content && sectionContent(section.content)}
                {section.points && (
                  <ul
                    style={{
                      margin: '0px',
                    }}
                  >
                    {section.points.map((point, idx) => (
                      <li key={idx}>{sectionContent(point)}</li>
                    ))}
                  </ul>
                )}
                {section.dos && (
                  <>
                    {section.dos.map((point, idx) =>
                      idx === 0 ? (
                        <Typography
                          fontSize="12px"
                          lineHeight="14px"
                          fontType="normal"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                        >
                          {point}
                        </Typography>
                      ) : (
                        <Grid
                          key={idx}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '4px',
                            ...(idx === section.dos.length - 1 && {
                              marginBottom: '4px',
                            }),
                          }}
                        >
                          <CheckBox htmlColor="#219464" />
                          {sectionContent(point)}
                        </Grid>
                      ),
                    )}
                    {section.donts.map((point, idx) =>
                      idx === 0 ? (
                        <Typography
                          fontSize="12px"
                          lineHeight="14px"
                          fontType="normal"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                        >
                          {point}
                        </Typography>
                      ) : (
                        <Grid
                          key={idx}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '4px',
                            alignItems: 'center',
                          }}
                        >
                          <Close width={'8px'} height={'8px'} htmlColor="red" />
                          {sectionContent(point)}
                        </Grid>
                      ),
                    )}
                  </>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

export default TermsModal;
